import { useState, useEffect, useRef } from 'react'
import { api } from '../api.js'
import { getChildToken, getStoredToken } from '../auth.js'
import InstallPWAButton from '../components/InstallPWAButton.jsx'
import ConfettiBurst from '../components/ConfettiBurst.jsx'
import XpPopup from '../components/XpPopup.jsx'
import { cacheChildQuests, getCachedChildQuests, queueOfflineCompletion, getPendingCount } from '../offlineSync.js'
import { useTTS } from '../hooks/useTTS.js'
import { useSound } from '../hooks/useSound.js'
import GuideCharacter from '../components/GuideCharacter.jsx'
import { showToast } from '../components/Toast.jsx'

const THEME_STYLES = {
  space: {
    bg: 'from-indigo-950 via-slate-900 to-slate-950',
    accent: 'text-indigo-300',
    accentBg: 'bg-indigo-500',
    ring: 'ring-indigo-300',
    shadow: 'shadow-indigo-500/30',
    bar: 'bg-indigo-400',
    cardBorder: 'border-indigo-500/20',
    filterActive: 'bg-indigo-500 ring-2 ring-indigo-300 scale-110 shadow-lg shadow-indigo-500/30',
    speakActive: 'text-indigo-200 bg-indigo-500/20 ring-1 ring-indigo-400/50',
    button: 'bg-indigo-600 hover:bg-indigo-500',
    speakingRing: 'border-indigo-400/60 ring-2 ring-indigo-400/40',
    emoji: '🚀',
  },
  fantasy: {
    bg: 'from-purple-950 via-slate-900 to-slate-950',
    accent: 'text-purple-300',
    accentBg: 'bg-purple-500',
    ring: 'ring-purple-300',
    shadow: 'shadow-purple-500/30',
    bar: 'bg-purple-400',
    cardBorder: 'border-purple-500/20',
    filterActive: 'bg-purple-500 ring-2 ring-purple-300 scale-110 shadow-lg shadow-purple-500/30',
    speakActive: 'text-purple-200 bg-purple-500/20 ring-1 ring-purple-400/50',
    button: 'bg-purple-600 hover:bg-purple-500',
    speakingRing: 'border-purple-400/60 ring-2 ring-purple-400/40',
    emoji: '🧙',
  },
  pirates: {
    bg: 'from-amber-950 via-slate-900 to-slate-950',
    accent: 'text-amber-300',
    accentBg: 'bg-amber-500',
    ring: 'ring-amber-300',
    shadow: 'shadow-amber-500/30',
    bar: 'bg-amber-400',
    cardBorder: 'border-amber-500/20',
    filterActive: 'bg-amber-500 ring-2 ring-amber-300 scale-110 shadow-lg shadow-amber-500/30',
    speakActive: 'text-amber-200 bg-amber-500/20 ring-1 ring-amber-400/50',
    button: 'bg-amber-600 hover:bg-amber-500',
    speakingRing: 'border-amber-400/60 ring-2 ring-amber-400/40',
    emoji: '🏴‍☠️',
  },
  nature: {
    bg: 'from-emerald-950 via-slate-900 to-slate-950',
    accent: 'text-emerald-300',
    accentBg: 'bg-emerald-500',
    ring: 'ring-emerald-300',
    shadow: 'shadow-emerald-500/30',
    bar: 'bg-emerald-400',
    cardBorder: 'border-emerald-500/20',
    filterActive: 'bg-emerald-500 ring-2 ring-emerald-300 scale-110 shadow-lg shadow-emerald-500/30',
    speakActive: 'text-emerald-200 bg-emerald-500/20 ring-1 ring-emerald-400/50',
    button: 'bg-emerald-600 hover:bg-emerald-500',
    speakingRing: 'border-emerald-400/60 ring-2 ring-emerald-400/40',
    emoji: '🌿',
  },
  default: {
    bg: 'from-violet-900 via-slate-900 to-slate-900',
    accent: 'text-violet-300',
    accentBg: 'bg-violet-500',
    ring: 'ring-violet-300',
    shadow: 'shadow-violet-500/30',
    bar: 'bg-violet-400',
    cardBorder: 'border-violet-500/20',
    filterActive: 'bg-violet-500 ring-2 ring-violet-300 scale-110 shadow-lg shadow-violet-500/30',
    speakActive: 'text-violet-200 bg-violet-500/20 ring-1 ring-violet-400/50',
    button: 'bg-violet-600 hover:bg-violet-500',
    speakingRing: 'border-violet-400/60 ring-2 ring-violet-400/40',
    emoji: '⭐',
  },
}

export default function ChildHome({ child, children, theme, onSwitchChild, locked = false }) {
  const [quests, setQuests] = useState([])
  const [goals, setGoals] = useState([])
  const [loading, setLoading] = useState(true)
  const [isOffline, setIsOffline] = useState(false)
  const [starting, setStarting] = useState(null)
  const [done, setDone] = useState(null)
  const [confirmQuest, setConfirmQuest] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [showConfetti, setShowConfetti] = useState(false)
  const [xpPopup, setXpPopup] = useState(null) // { amount, key }
  const { play: playSound } = useSound()
  const [pendingSync, setPendingSync] = useState(0)
  const [league, setLeague] = useState([])
  const [filterCat, setFilterCat] = useState('all')
  const [visibleCount, setVisibleCount] = useState(8)
  const [speakingId, setSpeakingId] = useState(null)

  const { speak, speaking, supported, cancel } = useTTS()

  const t = THEME_STYLES[theme] ?? THEME_STYLES.default

  const CATEGORY_EMOJIS = {
    all:       { emoji: '⭐', label: 'Alla'    },
    physical:  { emoji: '🏃', label: 'Rörelse' },
    creative:  { emoji: '🎨', label: 'Skapa'   },
    cognitive: { emoji: '🧠', label: 'Tänka'   },
    language:  { emoji: '💬', label: 'Prata'   },
    family:    { emoji: '👨‍👩‍👧', label: 'Familj'  },
    social:    { emoji: '🤝', label: 'Vänner'  },
  }

  useEffect(() => {
    async function loadQuests() {
      // Try network first
      if (navigator.onLine) {
        try {
           const [qData, gData] = await Promise.all([
             api.quests.list(),
             api.goals.list().catch(() => []),
           ])
          const allQuests = qData.quests || qData || []
          // Cache only child-independent quests
           const independent = allQuests.filter(q => !q.requires_parent)
           await cacheChildQuests(independent)
           setQuests(independent)
           setGoals(gData || [])
           setIsOffline(false)
        } catch {
          // Network failed — fall through to cache
          await loadFromCache()
        }
      } else {
        await loadFromCache()
      }
      setLoading(false)
    }

    async function loadFromCache() {
      const cached = await getCachedChildQuests(child.birth_year, child.birth_month)
      setQuests(cached)
      setIsOffline(true)
    }

    loadQuests()
    getPendingCount().then(setPendingSync)
    // Load sibling league (non-blocking — don't let it fail quest loading)
    api.family.siblingLeague()
      .then(data => setLeague(data.children || []))
      .catch(() => {}) // offline — silently skip
  }, [child.id, child.birth_year, child.birth_month])

  // Reset speakingId when TTS finishes
  useEffect(() => {
    if (!speaking) setSpeakingId(null)
  }, [speaking])

  function handleSpeak(quest) {
    if (speakingId === quest.id) {
      cancel()
      setSpeakingId(null)
    } else {
      const text = [quest.story_title || quest.title, quest.story_description || quest.description || ''].filter(Boolean).join('. ')
      setSpeakingId(quest.id)
      speak(text)
    }
  }

  function triggerCompletion(quest) {
    setDone(quest.id)
    setTimeout(() => setDone(null), 3000)
    playSound('quest-complete')
    setShowConfetti(true)
    setTimeout(() => setShowConfetti(false), 2000)
    setXpPopup({ amount: quest.xp, key: Date.now() })
    setTimeout(() => setXpPopup(null), 2500)
  }

  // Opens the confirmation bottom sheet
  function handleStart(quest) {
    setConfirmQuest(quest)
  }

  // Called from bottom sheet — proofFiles is [] or [File]
  async function handleComplete(quest, proofFiles = []) {
    setUploading(true)
    setStarting(quest.id)
    try {
      if (navigator.onLine) {
        await api.quests.complete(
          quest.id,
          child.id,
          '',
          proofFiles,
          false,
          null,
        )
      } else {
        const token = getChildToken()
        if (!token) {
          showToast('Kunde inte spara — barnets session har gått ut.')
          return
        }
        await queueOfflineCompletion({
          questId: quest.id,
          childId: child.id,
          notes: '',
          proofs: proofFiles,
          shareWithGroup: false,
          collaboratingFamilyId: null,
          token,
        })
        setPendingSync(c => c + 1)
      }
      triggerCompletion(quest)
    } catch (err) {
      if (err.name === 'TypeError' || err.message?.includes('fetch')) {
        try {
          const token = getChildToken()
          if (!token) {
            showToast('Kunde inte spara — barnets session har gått ut.')
            return
          }
          await queueOfflineCompletion({
            questId: quest.id,
            childId: child.id,
            notes: '',
            proofs: proofFiles,
            shareWithGroup: false,
            collaboratingFamilyId: null,
            token,
          })
          setPendingSync(c => c + 1)
          triggerCompletion(quest)
        } catch {
          showToast('Kunde inte spara uppdraget offline.')
        }
      } else {
        showToast('Något gick fel. Försök igen.')
      }
    } finally {
      setUploading(false)
      setStarting(null)
      setConfirmQuest(null)
    }
  }

  const activeGoal = goals.find(g => g.status === 'active')

  const filteredQuests = filterCat === 'all'
    ? quests
    : quests.filter(q => q.category === filterCat)

  const visibleQuests = filteredQuests.slice(0, visibleCount)

  function handleFilterChange(cat) {
    setFilterCat(cat)
    setVisibleCount(8)
  }

  return (
    <div className={`min-h-screen bg-gradient-to-b ${t.bg}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-safe pt-4 pb-3">
        <div className="flex items-center gap-3">
          <span className="text-4xl">{child.avatar_emoji || '🦁'}</span>
          <div>
            <p className="text-lg font-bold text-white">{child.name}</p>
            {activeGoal && (
              <p className={`text-xs ${t.accent}`}>{activeGoal.title}</p>
            )}
          </div>
        </div>
         <div className="flex items-center gap-2">
           {pendingSync > 0 && (
             <span className="rounded-full bg-amber-500/20 px-2.5 py-1 text-xs font-bold text-amber-300">
               {pendingSync} väntar
             </span>
           )}
           <InstallPWAButton />
           {!locked && (
             <button
               onClick={onSwitchChild}
               className="rounded-xl bg-white/10 px-3 py-1.5 text-xs font-medium text-white/80 hover:bg-white/20"
             >
               Byt
             </button>
           )}
         </div>
      </div>

      {/* Guide companion */}
      <div className="flex items-center gap-3 mx-4 mb-3 rounded-2xl bg-white/8 backdrop-blur-md border border-white/15 px-3 py-2">
        <GuideCharacter theme={theme} size="sm" />
        <div>
          <p className="text-xs font-semibold text-white/90">
            {theme === 'space' ? 'Pixel' : theme === 'fantasy' ? 'Gnista' : theme === 'pirates' ? 'Kapten Kex' : theme === 'nature' ? 'Lysa' : 'Guiden'}
          </p>
          <p className="text-[11px] text-white/50 leading-snug">
            {activeGoal ? `Mål: ${activeGoal.title}` : 'Välj ett uppdrag och ge dig ut!'}
          </p>
        </div>
      </div>

      {/* Offline indicator */}
      {isOffline && (
        <div className="mx-4 mb-3 rounded-xl bg-amber-500/10 border border-amber-500/20 px-3 py-2 text-xs text-amber-300 text-center">
          📴 Offline — visar sparade uppdrag. Dina genomföranden skickas när du är online igen.
        </div>
      )}

      {/* Goal progress bar */}
       {activeGoal && (
         <div className="mx-4 mb-4">
           <div className={`flex justify-between text-xs ${t.accent} mb-1`}>
             <span>🎯 {activeGoal.title}</span>
             <span>{Math.min(activeGoal.current_xp || 0, activeGoal.xp_target)}/{activeGoal.xp_target} XP</span>
           </div>
           <div className="h-2 rounded-full bg-white/10 overflow-hidden">
             <div
               className={`h-full rounded-full ${t.bar} transition-all duration-700`}
               style={{ width: `${Math.min(100, Math.round(((activeGoal.current_xp || 0) / activeGoal.xp_target) * 100))}%` }}
             />
           </div>
         </div>
       )}

      {/* Sibling league */}
      {league.length >= 2 && (
        <div className="mx-4 mb-5">
          <h2 className={`text-sm font-semibold ${t.accent} uppercase tracking-wider mb-2`}>
            🏆 Veckans syskonliga
          </h2>
          <div className="rounded-2xl bg-white/8 backdrop-blur-sm border border-white/15 overflow-hidden">
            {(() => {
              const me = league.find(s => s.id === child.id)
              const others = league.filter(s => s.id !== child.id)
              const displayed = me ? [me, ...others] : others
              const maxXp = Math.max(...league.map(s => s.weekly_xp), 1)
              const medals = ['🥇', '🥈', '🥉']
              // XP rank for medal (based on original sorted league)
              const xpRank = Object.fromEntries(league.map((s, i) => [s.id, i]))
              return displayed.map((sibling, index) => {
                const isCurrentChild = sibling.id === child.id
                const pct = Math.round((sibling.weekly_xp / maxXp) * 100)
                const rank = xpRank[sibling.id]
                const medal = isCurrentChild ? '⭐' : (medals[rank] || `${rank + 1}.`)
                return (
                  <div
                    key={sibling.id}
                    className={`flex items-center gap-3 px-4 py-3 ${
                      index < displayed.length - 1 ? 'border-b border-white/5' : ''
                    } ${isCurrentChild ? `${t.accentBg}/10` : ''}`}
                  >
                    <span className="text-lg w-6 text-center flex-none">{medal}</span>
                    <span className="text-2xl flex-none">{sibling.avatar_emoji || '🦁'}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-sm font-semibold truncate ${isCurrentChild ? t.accent : 'text-white/80'}`}>
                          {isCurrentChild ? 'Du' : sibling.name}
                        </span>
                        <span className="text-xs font-bold text-amber-300 ml-2 flex-none">
                          {sibling.weekly_xp} XP
                        </span>
                      </div>
                      <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-700 ${isCurrentChild ? t.bar : 'bg-white/30'}`}
                          style={{ width: `${Math.max(pct, sibling.weekly_xp > 0 ? 4 : 0)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                )
              })
            })()}
          </div>
        </div>
      )}

      {/* Quest list */}
      <div className="px-4 pb-24">
        <h2 className={`text-sm font-semibold ${t.accent} uppercase tracking-wider mb-3`}>Dina uppdrag</h2>

        {/* Category filter — horizontal emoji buttons, py-3 gives room for scale-110 */}
        <div className="-mx-4 mb-3 overflow-x-auto scrollbar-none">
          <div className="flex gap-3 px-4 py-3">
            {Object.entries(CATEGORY_EMOJIS).map(([cat, { emoji, label }]) => {
              const isActive = filterCat === cat
              return (
                <button
                  key={cat}
                  onClick={() => handleFilterChange(cat)}
                  className={`flex-none flex flex-col items-center justify-center gap-1 w-16 h-16 rounded-2xl transition-all duration-200 select-none ${
                    isActive
                      ? t.filterActive
                      : 'bg-white/10 hover:bg-white/20 active:scale-95'
                  }`}
                >
                  <span style={{ fontSize: '1.75rem', lineHeight: 1 }}>{emoji}</span>
                  <span className="text-[10px] font-semibold text-white/80 leading-none">{label}</span>
                </button>
              )
            })}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-violet-500 border-t-transparent" />
          </div>
        ) : quests.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            {!isOffline && (
              <div className="flex justify-center mb-3">
                <GuideCharacter theme={theme} size="md" message="Inga uppdrag just nu!" />
              </div>
            )}
            {isOffline && <p className="text-4xl mb-3">📴</p>}
            {isOffline && <p className="font-semibold text-white">Inga sparade uppdrag</p>}
            {isOffline && <p className="text-sm mt-1">Anslut till internet för att ladda uppdrag.</p>}
            {!isOffline && <p className="text-sm mt-1 text-white/50">Kolla igen senare.</p>}
          </div>
        ) : filteredQuests.length === 0 ? (
          <div className="text-center py-8">
            <div className="flex justify-center mb-2">
              <GuideCharacter theme={theme} size="sm" message="Inga uppdrag här" />
            </div>
            <p className="text-white/60 text-sm mt-1">Prova en annan kategori!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {visibleQuests.map(quest => (
              <div
                key={quest.id}
                onClick={supported ? () => handleSpeak(quest) : undefined}
                className={`relative flex flex-col rounded-2xl bg-white/8 backdrop-blur-sm border overflow-hidden transition hover:bg-white/12 ${
                  supported ? 'cursor-pointer active:scale-[0.97]' : ''
                } ${speakingId === quest.id
                  ? `${t.speakingRing} animate-ring-pulse`
                  : t.cardBorder
                }`}
              >
                {/* XP badge — absolute top-right */}
                <span className="absolute top-2 right-2 z-10 rounded-lg bg-amber-500/25 px-1.5 py-0.5 text-[10px] font-bold text-amber-300 leading-tight">
                  +{quest.xp}✨
                </span>

                {/* Body */}
                <div className="flex-1 p-3 pb-2">
                  <h3 className="font-bold text-sm leading-snug pr-8 text-white">
                    {speakingId === quest.id && <span className="mr-1 text-xs">🔊</span>}
                    {quest.story_title || quest.title}
                  </h3>
                  {(quest.story_description || quest.description) && (
                    <p className="mt-1.5 text-xs leading-relaxed line-clamp-2 text-slate-400">
                      {quest.story_description || quest.description}
                    </p>
                  )}
                </div>

                {/* CTA — pinned to bottom */}
                {done === quest.id ? (
                  <div
                    onClick={e => e.stopPropagation()}
                    className="mx-2 mb-2 flex items-center justify-center gap-1 rounded-xl bg-emerald-500/20 py-2 text-xs font-bold text-emerald-300"
                  >
                    ✅ {isOffline ? 'Sparas!' : 'Skickat!'}
                  </div>
                ) : (
                  <button
                    onClick={e => { e.stopPropagation(); handleStart(quest) }}
                    disabled={starting === quest.id}
                    className={`mx-2 mb-2 rounded-xl ${t.button} py-2.5 text-xs font-bold text-white transition active:scale-95 disabled:opacity-50`}
                  >
                    {starting === quest.id ? '...' : 'Jag gjorde det! 🎉'}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Show more button */}
        {!loading && filteredQuests.length > visibleCount && (
          <button
            onClick={() => setVisibleCount(v => v + 8)}
            className="mt-4 w-full rounded-2xl bg-white/10 py-4 text-base font-bold text-white/70 hover:bg-white/20 active:scale-95 transition"
          >
            Fler uppdrag ✨
          </button>
        )}
      </div>

      {/* Completion celebration */}
      {showConfetti && <ConfettiBurst />}
      {xpPopup && <XpPopup key={xpPopup.key} amount={xpPopup.amount} />}

      {/* Photo proof bottom sheet */}
      {confirmQuest && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center"
          onClick={() => !uploading && setConfirmQuest(null)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

          {/* Sheet */}
          <div
            className={`relative w-full max-w-lg rounded-t-3xl bg-slate-950/95 backdrop-blur-xl border-t ${t.cardBorder} p-6 pb-safe pb-8`}
            onClick={e => e.stopPropagation()}
          >
            {/* Quest title recap */}
            <p className="text-center text-white/50 text-xs mb-1">Uppdrag klart?</p>
            <h3 className="text-center font-bold text-white text-lg mb-8 leading-snug px-2">
              {confirmQuest.story_title || confirmQuest.title}
            </h3>

            <div className="flex gap-4">
              {/* Photo button */}
              <label className={`flex flex-col items-center justify-center gap-2 flex-1 rounded-2xl ${t.button} py-6 font-bold text-white cursor-pointer active:scale-95 transition ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
                <span className="text-5xl">📷</span>
                <span className="text-sm">Bild!</span>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="sr-only"
                  onChange={e => {
                    const file = e.target.files?.[0]
                    if (file) handleComplete(confirmQuest, [file])
                  }}
                />
              </label>

              {/* No-photo button */}
              <button
                onClick={() => handleComplete(confirmQuest, [])}
                disabled={uploading}
                className="flex flex-col items-center justify-center gap-2 flex-1 rounded-2xl bg-white/10 py-6 font-bold text-white/80 hover:bg-white/20 active:scale-95 transition disabled:opacity-50"
              >
                {uploading ? (
                  <>
                    <span className="h-10 w-10 animate-spin rounded-full border-4 border-white border-t-transparent" />
                    <span className="text-sm">Skickar…</span>
                  </>
                ) : (
                  <>
                    <span className="text-5xl">✅</span>
                    <span className="text-sm">Klart!</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
